import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, KeyRound, ArrowRight, Lock, Database, Quote, Fingerprint, ServerCog, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Brand } from "@/components/TopBar";
import { pub, tenantApi, LS } from "@/lib/apiClient";

const GUARANTEES = [
  { icon: ServerCog, text: "Raw memory never leaves the server" },
  { icon: Fingerprint, text: "Tenant-scoped retrieval with module licensing" },
  { icon: Quote, text: "Citations on every answer" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [tenantOpen, setTenantOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [tenantKey, setTenantKey] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState(null);

  useEffect(() => {
    pub.get("/health").then((r) => setRecords(r.data.indexed_records)).catch(() => {});
  }, []);

  const enterTenant = async () => {
    if (!tenantKey.trim()) return;
    setBusy(true);
    try {
      localStorage.setItem(LS.tenantKey, tenantKey.trim());
      const r = await tenantApi.get("/tenant/me");
      localStorage.setItem(LS.tenantName, r.data.name);
      toast.success(`Welcome, ${r.data.name}`);
      navigate("/console");
    } catch (e) {
      localStorage.removeItem(LS.tenantKey);
      toast.error("Invalid tenant key. Please check and try again.");
    } finally {
      setBusy(false);
    }
  };

  const enterAdmin = async () => {
    if (!adminKey.trim()) return;
    setBusy(true);
    try {
      localStorage.setItem(LS.adminKey, adminKey.trim());
      // validate
      const { adminApi } = await import("@/lib/apiClient");
      await adminApi.get("/admin/overview");
      toast.success("Admin access granted");
      navigate("/admin");
    } catch (e) {
      localStorage.removeItem(LS.adminKey);
      toast.error("Invalid admin key.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen app-noise">
      <div className="mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
        <Brand />
        <div className="text-xs text-muted-foreground font-mono">
          {records != null ? `${records.toLocaleString()} records indexed` : "•••"}
        </div>
      </div>

      <div className="mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center pt-10 lg:pt-20">
        {/* Left: brand + guarantees */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <svg viewBox="0 0 48 48" className="h-16 w-16 mb-5" role="img" aria-label="Persistent A.I. memory ring" data-testid="hero-animated-ring">
            <defs>
              <linearGradient id="pai-hero-ring" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#5EEAD4" />
                <stop offset="1" stopColor="#14B8A6" />
              </linearGradient>
            </defs>
            <circle
              cx="24" cy="24" r="16" pathLength="100"
              fill="none" stroke="url(#pai-hero-ring)" strokeWidth="3.4" strokeLinecap="round"
              className="pai-ring-anim"
            />
            <circle cx="24" cy="24" r="9.5" fill="#2DD4BF" opacity="0.14" className="pai-core-anim" />
            <circle cx="24" cy="24" r="5.4" fill="#2DD4BF" className="pai-core-anim" />
            <circle cx="36.8" cy="14.5" r="3.2" fill="#99F6E4" className="pai-sat-anim" />
          </svg>
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary mb-6">
            <ShieldCheck className="h-3.5 w-3.5" /> Zero-Exposure • Server-side Memory Engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight leading-[1.05]">
            Persistent <span className="text-primary">A.I.</span><br />
            <span className="text-2xl sm:text-3xl lg:text-4xl text-muted-foreground font-medium">Contextual Persistent Memory Engine</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-7">
            A secure, tenant-scoped knowledge base for your GVI Committee AI personas. Governed memory is
            vectorized on the server, sanitized of proprietary parameters, and retrieved strictly within
            each tenant&apos;s licensed modules.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
            {GUARANTEES.map((g, i) => {
              const Icon = g.icon;
              return (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card/60 p-4">
                  <Icon className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-sm text-muted-foreground">{g.text}</span>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Right: entry card */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <Card className="p-8 bg-card border-border" style={{ boxShadow: "var(--shadow-elev)" }}>
            <div className="font-display text-xl font-semibold">Enter the Command Center</div>
            <p className="text-sm text-muted-foreground mt-1">Choose how you want to access the engine.</p>

            <Link
              to="/welcome"
              data-testid="entry-avi-button"
              className="mt-6 w-full group flex items-center justify-between rounded-xl border border-primary/30 bg-primary/10 hover:bg-primary/15 p-4 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium">Meet Avi — your personal A.I.</div>
                  <div className="text-xs text-muted-foreground">Consumer app: sign up free, no GVI code needed</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="mt-3 space-y-3">
              <button
                onClick={() => setTenantOpen(true)}
                data-testid="entry-tenant-button"
                className="w-full group flex items-center justify-between rounded-xl border border-border bg-secondary/40 hover:bg-secondary p-4 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/30 grid place-items-center">
                    <KeyRound className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">Enter as Tenant</div>
                    <div className="text-xs text-muted-foreground">Chat with your licensed GVI personas</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => setAdminOpen(true)}
                data-testid="entry-admin-button"
                className="w-full group flex items-center justify-between rounded-xl border border-border bg-secondary/40 hover:bg-secondary p-4 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-secondary grid place-items-center">
                    <Database className="h-5 w-5 text-blue-300" />
                  </div>
                  <div>
                    <div className="font-medium">Admin Console</div>
                    <div className="text-xs text-muted-foreground">Ingest memory & manage tenant licensing</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <div className="mt-6 rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5 font-medium text-foreground mb-1"><Lock className="h-3 w-3" /> Demo access</div>
              Tenant keys: <span className="font-mono text-primary">gvi_exec_demo_key_2026</span>,{" "}
              <span className="font-mono text-primary">gvi_risk_demo_key_2026</span>,{" "}
              <span className="font-mono text-primary">gvi_invest_demo_key_2026</span>.{" "}
              <span data-testid="admin-key-notice">Admin key: provided privately to operators.</span>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Tenant dialog */}
      <Dialog open={tenantOpen} onOpenChange={setTenantOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Enter Tenant Key</DialogTitle>
            <DialogDescription>Your key determines which memory modules and personas you can access.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="gvi_..."
            value={tenantKey}
            onChange={(e) => setTenantKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && enterTenant()}
            data-testid="tenant-api-key-input"
            className="font-mono"
          />
          <DialogFooter>
            <Button onClick={enterTenant} disabled={busy} data-testid="tenant-api-key-submit">
              {busy ? "Verifying..." : "Enter Console"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin dialog */}
      <Dialog open={adminOpen} onOpenChange={setAdminOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Admin Access</DialogTitle>
            <DialogDescription>Enter the admin key to manage ingestion and tenant licensing.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            type="password"
            placeholder="admin key"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && enterAdmin()}
            data-testid="admin-api-key-input"
            className="font-mono"
          />
          <DialogFooter>
            <Button onClick={enterAdmin} disabled={busy} data-testid="admin-api-key-submit">
              {busy ? "Verifying..." : "Enter Admin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
